/**
 * Buyer Hub route guard — Hub-sent A1 only, bound to one corporate.
 * Operators may enter view-only preview with ?preview=1 (U8).
 */

import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useBuyerHubAccess } from "@/hooks/useBuyerHubAccess";
import { useCorporateAccess } from "@/hooks/useCorporateAccess";
import { useAuth } from "@/hooks/useAuth";
import { buyerHubHomePath } from "@/services/buyerHub/hubSend";
import { FlexLayout, Spinner } from "@/design-system/salt";

interface BuyerHubGuardProps {
  children: React.ReactNode;
}

/** Buyer Hub Guard component. */
export default function BuyerHubGuard({ children }: BuyerHubGuardProps) {
  const { corporateId: corporateIdParam } = useParams<{ corporateId: string }>();
  const [searchParams] = useSearchParams();
  const { hasRole } = useAuth();
  const { isHubBuyerUser, hubCorporateId, loading } = useBuyerHubAccess();
  const { hasCorporateAccess, loading: accessLoading } = useCorporateAccess();

  const isOperator = hasRole("admin") || hasRole("manager");
  const isPreview = searchParams.get("preview") === "1";
  const routeCorporateId = Number(corporateIdParam);

  if (loading || accessLoading) {
    return (
      <FlexLayout align="center" justify="center" style={{ minHeight: "100vh", background: "var(--salt-container-primary-background)" }}>
        <Spinner aria-label="Loading Buyer Hub" size="large" />
      </FlexLayout>
    );
  }

  if (isPreview && isOperator && Number.isFinite(routeCorporateId) && hasCorporateAccess(routeCorporateId)) {
    return <>{children}</>;
  }

  if (!isHubBuyerUser || hubCorporateId === null) {
    return <Navigate to="/auth" replace />;
  }

  if (!Number.isFinite(routeCorporateId) || routeCorporateId !== hubCorporateId) {
    return <Navigate to={buyerHubHomePath(hubCorporateId)} replace />;
  }

  if (!hasCorporateAccess(routeCorporateId)) {
    return <Navigate to={buyerHubHomePath(hubCorporateId)} replace />;
  }

  return <>{children}</>;
}
